import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Menu } from 'lucide-react'
import { Sidebar, SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED } from './sidebar'
import { CommandPalette, useCommandPalette } from './command-palette'
import { useMediaQuery } from '@/hooks/use-media-query'

const SIDEBAR_STORAGE_KEY = 'sentinel-sidebar-collapsed'

interface MainLayoutProps {
  children: React.ReactNode
  onLogout?: () => void
}

export function MainLayout({ children, onLogout }: MainLayoutProps) {
  const { open: commandOpen, setOpen: setCommandOpen } = useCommandPalette()

  const isMobile = useMediaQuery('(max-width: 767px)')

  // Persist desktop sidebar collapsed state in localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
    return stored === 'true'
  })

  // Mobile drawer open state
  const [mobileOpen, setMobileOpen] = useState(false)

  // Close mobile drawer when switching to desktop
  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false)
    }
  }, [isMobile])

  // Escape key closes the mobile drawer
  useEffect(() => {
    if (!isMobile) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isMobile])

  // Sync desktop collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarCollapsed))
  }, [sidebarCollapsed])

  const sidebarWidth = sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar — desktop: fixed in normal flow; mobile: off-canvas drawer */}
      {isMobile ? (
        <>
          {/* Backdrop */}
          {mobileOpen && (
            <div
              className="fixed inset-0 z-40 bg-foreground/40"
              onClick={() => setMobileOpen(false)}
              aria-hidden="true"
            />
          )}
          {/* Drawer — framer-motion on the inner aside drives left position */}
          <div
            id="mobile-sidebar"
            data-testid="mobile-sidebar"
            data-open={mobileOpen}
            className={mobileOpen ? '' : 'pointer-events-none'}
          >
            <Sidebar
              collapsed={false}
              onCollapsedChange={() => {/* no-op on mobile */}}
              onLogout={onLogout}
              onNavClick={() => setMobileOpen(false)}
              mobileOpen={mobileOpen}
              isMobile={true}
            />
          </div>
        </>
      ) : (
        <Sidebar
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
          onLogout={onLogout}
        />
      )}

      {/* Main content area */}
      {isMobile ? (
        <div className="flex flex-col min-h-screen">
          {/* Mobile top bar with hamburger */}
          <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background px-4">
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMobileOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-sidebar"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="font-semibold text-sm text-foreground">Sentinel</span>
          </header>
          {/* Phone: a 16px gutter, and a page can never be wider than the screen. A wide child scrolls inside its own
              box (a table's scroller); `overflow-x-clip` is the backstop so none can give the PAGE a horizontal scroll. */}
          <main className="min-w-0 max-w-full flex-1 overflow-x-clip p-4" data-testid="app-main">
            {children}
          </main>
        </div>
      ) : (
        <motion.div
          initial={false}
          animate={{ marginLeft: sidebarWidth }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="flex flex-col min-h-screen"
        >
          <main className="min-w-0 flex-1 p-6" data-testid="app-main">
            {children}
          </main>
        </motion.div>
      )}

      {/* Floating AI button — bottom right */}
      {/* Command palette (Cmd+K) */}
      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
    </div>
  )
}
